import { jwtDecode } from 'jwt-decode';

const ADMIN_TOKEN_STORAGE_KEY = 'encreage.admin.token';
const LEGACY_ADMIN_TOKEN_STORAGE_KEY = 'token';

function getStorage(type) {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return type === 'local' ? window.localStorage : window.sessionStorage;
  } catch {
    return null;
  }
}

function readStorageValue(storage, key) {
  if (!storage) {
    return '';
  }

  try {
    return `${storage.getItem(key) ?? ''}`.trim();
  } catch {
    return '';
  }
}

function removeStorageValue(storage, key) {
  if (!storage) {
    return;
  }

  try {
    storage.removeItem(key);
  } catch {
    // Ignore storage failures so auth cleanup stays best-effort.
  }
}

function clearStorageKeys(storage) {
  removeStorageValue(storage, ADMIN_TOKEN_STORAGE_KEY);
  removeStorageValue(storage, LEGACY_ADMIN_TOKEN_STORAGE_KEY);
}

export function clearAdminToken() {
  clearStorageKeys(getStorage('session'));
  clearStorageKeys(getStorage('local'));
}

export function writeAdminToken(token) {
  const normalizedToken = `${token ?? ''}`.trim();
  const sessionStorage = getStorage('session');
  const localStorage = getStorage('local');
  const preferredStorage = sessionStorage ?? localStorage;

  if (!normalizedToken || !preferredStorage) {
    clearAdminToken();
    return;
  }

  try {
    preferredStorage.setItem(ADMIN_TOKEN_STORAGE_KEY, normalizedToken);
  } catch {
    // Ignore storage failures and keep login flow functional.
  }

  if (preferredStorage !== sessionStorage) {
    try {
      preferredStorage.setItem(LEGACY_ADMIN_TOKEN_STORAGE_KEY, normalizedToken);
    } catch {
      // Ignore storage failures and keep login flow functional.
    }
  }

  if (sessionStorage && preferredStorage !== sessionStorage) {
    clearStorageKeys(sessionStorage);
  }

  if (localStorage && preferredStorage !== localStorage) {
    clearStorageKeys(localStorage);
  }
}

export function readAdminToken() {
  const sessionStorage = getStorage('session');
  const localStorage = getStorage('local');
  const sessionToken =
    readStorageValue(sessionStorage, ADMIN_TOKEN_STORAGE_KEY) ||
    readStorageValue(sessionStorage, LEGACY_ADMIN_TOKEN_STORAGE_KEY);

  if (sessionToken) {
    return sessionToken;
  }

  const fallbackToken =
    readStorageValue(localStorage, ADMIN_TOKEN_STORAGE_KEY) ||
    readStorageValue(localStorage, LEGACY_ADMIN_TOKEN_STORAGE_KEY);

  if (fallbackToken) {
    writeAdminToken(fallbackToken);
  }

  return fallbackToken;
}

export function isAdminTokenExpired(token) {
  if (!token) {
    return true;
  }

  try {
    const decoded = jwtDecode(token);
    return typeof decoded?.exp === 'number' && decoded.exp * 1000 <= Date.now();
  } catch {
    return true;
  }
}

export function getAdminAuthHeaders() {
  const token = readAdminToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export function hasActiveAdminSession() {
  const token = readAdminToken();

  if (isAdminTokenExpired(token)) {
    clearAdminToken();
    return false;
  }

  return true;
}
